import { useState, useMemo } from "react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";

type HeatmapMode = "pnl" | "winRate";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Theme-aware P&L cell colors built from CSS tokens with variable alpha.
const cellPositive = (alpha: number) => `hsl(var(--edgely-pnl-positive) / ${alpha})`;
const cellNegative = (alpha: number) => `hsl(var(--edgely-pnl-negative) / ${alpha})`;
const COLOR_POSITIVE = cellPositive(0.6);
const COLOR_NEGATIVE = cellNegative(0.6);
const COLOR_EMPTY_DARK = "rgba(255,255,255,0.04)";
const COLOR_EMPTY_LIGHT = "rgba(0,0,0,0.04)";
const INTENSITY_MIN = 0.1;
const INTENSITY_MAX = 0.7;

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

export default function HeatmapWidget() {
  const { analyticsData, isDark, t } = useDashboardData();
  const [heatmapMode, setHeatmapMode] = useState<HeatmapMode>("pnl");

  const maxAbsVal = useMemo(() => {
    if (!analyticsData || analyticsData.heatmapData.length === 0) return 1;
    return Math.max(
      ...analyticsData.heatmapData.map((c) =>
        Math.abs(heatmapMode === "pnl" ? c.pnl : c.winRate),
      ),
      1,
    );
  }, [analyticsData, heatmapMode]);

  if (!analyticsData || analyticsData.heatmapData.length === 0) {
    return (
      <Card className="h-full">
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const { heatmapData } = analyticsData;

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.analytics.heatmapTitle}
        isDark={isDark}
        rightContent={
          <div className="flex gap-1">
            {(["pnl", "winRate"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setHeatmapMode(mode)}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  heatmapMode === mode
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/30 text-muted-foreground"
                }`}
              >
                {mode === "pnl"
                  ? t.analytics.heatmapPnl
                  : t.analytics.heatmapWinRate}
              </button>
            ))}
          </div>
        }
      />
      <CardContent className="px-4 pb-4 pt-1 flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col overflow-x-auto overflow-y-hidden">
          <div className="min-w-[380px] flex-1 min-h-0 flex flex-col">
            {/* Hour labels */}
            <div className="flex ml-6 mb-0.5 shrink-0">
              {Array.from({ length: 24 }, (_, h) => (
                <div
                  key={h}
                  className="flex-1 text-center text-[8px] text-muted-foreground/60"
                >
                  {h % 3 === 0 ? h + "h" : ""}
                </div>
              ))}
            </div>

            {/* Day rows */}
            {DAY_LABELS.map((day, dIdx) => (
              <div key={day} className="flex items-stretch mb-px grow basis-0 min-h-[0.75rem]">
                <div className="w-6 shrink-0 flex items-center justify-end text-[9px] text-muted-foreground pr-1">
                  {day}
                </div>
                {Array.from({ length: 24 }, (_, hIdx) => {
                  const cell = heatmapData.find(
                    (c) => c.day === dIdx && c.hour === hIdx,
                  );
                  const val = cell
                    ? heatmapMode === "pnl"
                      ? cell.pnl
                      : cell.winRate
                    : null;
                  const intensity =
                    val !== null
                      ? Math.min(Math.abs(val) / maxAbsVal, 1)
                      : 0;
                  const isPositive =
                    heatmapMode === "pnl" ? (val ?? 0) >= 0 : (val ?? 0) >= 50;
                  const hasData = cell && cell.count > 0 && val !== null;

                  return (
                    <div
                      key={hIdx}
                      title={
                        cell
                          ? `${day} ${hIdx}h — ${
                              heatmapMode === "pnl"
                                ? cell.pnl.toFixed(2) + "$"
                                : cell.winRate.toFixed(0) + "% WR"
                            } (${cell.count} trades)`
                          : ""
                      }
                      className="flex-1 h-full rounded-sm mx-px"
                      style={{
                        background: hasData
                          ? isPositive
                            ? cellPositive(INTENSITY_MIN + intensity * INTENSITY_MAX)
                            : cellNegative(INTENSITY_MIN + intensity * INTENSITY_MAX)
                          : isDark
                          ? COLOR_EMPTY_DARK
                          : COLOR_EMPTY_LIGHT,
                      }}
                    />
                  );
                })}
              </div>
            ))}

            {/* Legend */}
            <div className="flex items-center gap-3 mt-1.5 shrink-0 text-[9px] text-muted-foreground">
              <div className="flex items-center gap-1">
                <div
                  className="w-2.5 h-2.5 rounded-sm"
                  style={{ background: COLOR_NEGATIVE }}
                />
                {heatmapMode === "pnl"
                  ? t.analytics.heatmapPnl + " (-)"
                  : t.analytics.heatmapLegendBelow50}
              </div>
              <div className="flex items-center gap-1">
                <div
                  className="w-2.5 h-2.5 rounded-sm"
                  style={{ background: COLOR_POSITIVE }}
                />
                {heatmapMode === "pnl"
                  ? t.analytics.heatmapPnl + " (+)"
                  : t.analytics.heatmapLegendAbove50}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
